import React from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label?: string;
  error?: string;
  theme?: 'pink' | 'orange' | 'teal';
  disabled?: boolean;
  required?: boolean;
}

const themeClasses = {
  pink: {
    button: 'focus:ring-pink-500 focus:border-pink-500',
    optionActive: 'bg-pink-50 text-pink-900',
    check: 'text-pink-600',
  },
  orange: {
    button: 'focus:ring-orange-500 focus:border-orange-500',
    optionActive: 'bg-orange-50 text-orange-900',
    check: 'text-orange-600',
  },
  teal: {
    button: 'focus:ring-teal-500 focus:border-teal-500',
    optionActive: 'bg-teal-50 text-teal-900',
    check: 'text-teal-600',
  },
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  theme = 'pink',
  disabled = false,
  required = false,
}) => {
  const selected = value || [];
  const themeStyle = themeClasses[theme];

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
        : `${selected.length} selected`;

  return (
    <div className="relative">
      {label && (
        <>
          <div className="row">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </>
      )}
      <Listbox value={selected} onChange={onChange} multiple disabled={disabled}>
        <Listbox.Button
          className={`relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${themeStyle.button} ${error ? 'border-red-500' : ''}`}
        >
          <span className={`block truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
            {displayText}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Listbox.Options className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {options.map(option => (
            <Listbox.Option
              key={option.value}
              value={option.value}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? themeStyle.optionActive : 'text-gray-900'}`
              }
            >
              {({ selected: isSelected }) => (
                <>
                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                    {option.label}
                  </span>
                  {isSelected ? (
                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${themeStyle.check}`}>
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default MultiSelectDropdown;
